import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, Undo2, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const BRUT = "border-2 border-[#0F172A] shadow-[6px_6px_0_#0F172A]";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const tok = params.get("tok") || "";
  const [state, setState] = useState({ loading: true, error: "", info: null });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // "confirm" | "all" | "undo" | null

  useEffect(() => {
    if (!tok) {
      setState({ loading: false, error: "Brak tokenu w linku.", info: null });
      return;
    }
    axios.get(`${API}/api/unsubscribe/preview?tok=${encodeURIComponent(tok)}`)
      .then(({ data }) => setState({ loading: false, error: "", info: data }))
      .catch((e) => setState({
        loading: false, info: null,
        error: e.response?.data?.detail || "Nieprawidłowy lub wygasły link.",
      }));
  }, [tok]);

  const post = async (path, extra = {}) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/unsubscribe/${path}`, { tok, ...extra });
      setDone(path === "resubscribe" ? "undo" : (extra.all ? "all" : "confirm"));
    } catch (e) {
      setState((s) => ({ ...s, error: e.response?.data?.detail || "Błąd żądania" }));
    } finally {
      setBusy(false);
    }
  };

  const catLabel = {
    welcome: "e-maile powitalne",
    digest: "cotygodniowe podsumowania administracyjne",
    proposal_status: "powiadomienia o statusie Twoich propozycji pytań",
    product_update: "aktualizacje produktu",
  }[state.info?.category] || state.info?.category;

  return (
    <div className="min-h-screen bg-[#FFFBEB] flex items-center justify-center p-6" data-testid="unsub-page">
      <div className={`${BRUT} bg-white rounded-md p-8 max-w-lg w-full`}>
        <div className="mb-4 text-xs tracking-[0.15em] uppercase font-bold text-[#64748B]">
          Kartkówka
        </div>
        <h1 className="font-display font-black text-3xl mb-4">Wypisanie z powiadomień</h1>

        {state.loading && (
          <div className="flex items-center gap-2 text-[#64748B]" data-testid="unsub-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Ładuję…
          </div>
        )}

        {!state.loading && state.error && (
          <div className="flex items-start gap-2 bg-[#FEE2E2] text-[#7F1D1D] p-3 rounded-md border-2 border-[#7F1D1D]"
               data-testid="unsub-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
            <div className="text-sm">{state.error}</div>
          </div>
        )}

        {!state.loading && state.info && !done && (
          <div data-testid="unsub-form">
            <p className="text-sm leading-relaxed mb-6">
              Adres <b>{state.info.email}</b> zostanie wypisany z listy: <b>{catLabel}</b>.
              {state.info.already_unsubscribed && (
                <span className="block mt-2 text-[#065F46] font-bold">
                  (Już jesteś wypisany z tej kategorii — możesz też wypisać się ze wszystkich.)
                </span>
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => post("confirm")}
                disabled={busy || state.info.already_unsubscribed}
                className="brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md flex-1"
                data-testid="unsub-confirm-btn"
              >
                {busy ? "Zapisuję…" : "Wypisz z tej kategorii"}
              </Button>
              <Button
                onClick={() => post("confirm", { all: true })}
                disabled={busy}
                variant="outline"
                className="brut border-2 border-[#0F172A] bg-[#FEE2E2] hover:bg-[#FEE2E2] text-[#7F1D1D] font-bold rounded-md flex-1"
                data-testid="unsub-all-btn"
              >
                Wypisz ze wszystkiego
              </Button>
            </div>
            <div className="mt-6 text-xs text-[#64748B] leading-relaxed">
              Wypisanie nie dotyczy powiadomień <b>transakcyjnych</b> (potwierdzenie e-mail,
              reset hasła, alerty bezpieczeństwa) — te są konieczne do korzystania z konta.
            </div>
          </div>
        )}

        {done === "confirm" && (
          <SuccessBlock
            title="Gotowe! Zostałeś wypisany z tej kategorii."
            info={state.info}
            onUndo={() => { setDone(null); post("resubscribe"); }}
          />
        )}
        {done === "all" && (
          <SuccessBlock
            title="Wypisano ze wszystkich powiadomień marketingowych."
            info={state.info}
            onUndo={() => { setDone(null); post("resubscribe"); }}
          />
        )}
        {done === "undo" && (
          <div className="bg-[#D1FAE5] border-2 border-[#065F46] text-[#065F46] p-4 rounded-md"
               data-testid="unsub-undone">
            <div className="flex items-center gap-2 font-bold mb-1">
              <Undo2 className="w-4 h-4" strokeWidth={2.5} /> Cofnięto
            </div>
            <div className="text-sm">Znów będziesz otrzymywać maile z tej kategorii.</div>
          </div>
        )}

        <div className="mt-8 pt-4 border-t border-[#E2E8F0] text-xs text-[#64748B]">
          <Link to="/" className="underline">← Powrót do kartkowka.pl</Link>
        </div>
      </div>
    </div>
  );
}

function SuccessBlock({ title, info, onUndo }) {
  return (
    <div className="bg-[#D1FAE5] border-2 border-[#065F46] p-4 rounded-md"
         data-testid="unsub-success">
      <div className="flex items-center gap-2 font-black text-[#065F46] mb-2">
        <CheckCircle2 className="w-5 h-5" strokeWidth={2.5} /> {title}
      </div>
      <p className="text-sm text-[#065F46] mb-3">
        Adres <b>{info.email}</b> nie będzie już otrzymywał tego typu powiadomień.
      </p>
      <Button
        onClick={onUndo}
        variant="outline"
        className="brut border-2 border-[#065F46] bg-white hover:bg-white text-[#065F46] font-bold rounded-md h-9 px-3 text-xs"
        data-testid="unsub-undo-btn"
      >
        <Undo2 className="w-3 h-3 mr-1" strokeWidth={2.5} /> Cofnij wypisanie
      </Button>
    </div>
  );
}
